import {
  Alert,
  AlertProps,
  formatDate,
  Icon,
  InfoDrawerMeta,
} from "@inductiveautomation/ws-components";
import { TFunction } from "i18next";
import { capitalize } from "lodash";
import { useTranslation } from "react-i18next";

import {
  Certification,
  CertificationActionCallback,
  ElectiveWithType,
} from "../Training.types";
import { getDrawerContentStyles } from "../_training.styles";
import HistorySection from "./HistorySection";

const alertProps = (
  t: TFunction,
  classes: ReturnType<typeof getDrawerContentStyles>["classes"],
  certification: Exclude<Certification, ElectiveWithType>,
  onAction: CertificationActionCallback,
  canPurchaseOnline: boolean
): Omit<AlertProps, "testId" | "closeButtonEventHandler"> | undefined => {
  const { type, status } = certification;
  const expiration = certification.expirationDate
    ? formatDate(new Date(certification.expirationDate), "date")
    : "-";

  if (type === "CREDENTIAL" && status === "UPDATE_AVAILABLE") {
    return {
      id: "credential-update-alert",
      title: t("training.credential.update_available.heading"),
      description: t("training.credential.update_available.description"),
      variant: "neutral",
      actions: [
        {
          label: t("training.actions.visit_inductive_university"),
          icon: <Icon name="open" className={classes.alertActionIcon} />,
          onClick: () =>
            onAction("visit-inductive-university", { certificationId: type }),
        },
      ],
    };
  }

  const actions = canPurchaseOnline
    ? [
        {
          label: t("training.actions.purchase_test.label"),
          onClick: () =>
            onAction("purchase-test", { certificationPurchaseType: type }),
        },
      ]
    : [];

  if (type === "CORE" && status === "EXPIRES_SOON") {
    let alertDescription = t("training.core.expires_soon.description");

    if (
      certification.goldStatus == "IN_PROGRESS" ||
      certification.goldStatus == "EXPIRED"
    ) {
      alertDescription = t("training.core.gold_in_progress.description");
    }

    return {
      id: "core-certificate-expires-soon-alert",
      title: t("training.core.expires_soon.heading", {
        date: expiration,
      }),
      description: alertDescription,
      variant: "warning",
      actions,
    };
  }

  if (type === "CORE" && status === "EARNED") {
    if (
      certification.goldStatus === "EARNED" ||
      certification.goldStatus === "EXPIRES_SOON"
    ) {
      return {
        id: "core-certification-expired-alert",
        description: t("training.core.gold_active.description"),
        variant: "info",
      };
    }
  }

  if (type === "CORE" && status === "EXPIRED") {
    let alertDescription = t("training.core.expired.description");

    if (certification.goldStatus === "IN_PROGRESS") {
      alertDescription = t("training.core.gold_in_progress.description");
    }
    if (certification.goldStatus === "EXPIRED") {
      alertDescription = t("training.gold.expired.description");
    }
    return {
      id: "core-certification-expired-alert",
      title: t("training.core.expired.heading", {
        date: expiration,
      }),
      description: alertDescription,
      variant: "error",
      actions,
    };
  }

  if (type === "GOLD" && status === "EXPIRES_SOON") {
    return {
      id: "gold-certificate-expires-soon-alert",
      title: t("training.gold.expires_soon.heading", {
        date: expiration,
      }),
      description: t("training.gold.expires_soon.description"),
      variant: "warning",
      actions: [
        {
          label: t("training.actions.purchase_test.label"),
          onClick: () =>
            onAction("purchase-test", { certificationPurchaseType: type }),
        },
      ],
    };
  }

  if (type === "GOLD" && status === "INVALID") {
    return {
      id: "gold-certification-invalid-alert",
      title: t("training.gold.invalid.heading"),
      description: t("training.gold.invalid.description"),
      variant: "warning",
    };
  }

  if (type === "GOLD" && status === "EXPIRED") {
    return {
      id: "gold-certification-expired-alert",
      title: t("training.gold.expired.heading", {
        date: expiration,
      }),
      description: t("training.gold.expired.description"),
      variant: "error",
      actions,
    };
  }
};

const EarnedContentAlert = ({
  certification,
  onAction,
  canPurchaseOnline,
}: {
  certification: Exclude<Certification, ElectiveWithType>;
  onAction: CertificationActionCallback;
  canPurchaseOnline: boolean;
}) => {
  const { t } = useTranslation();
  const { classes } = getDrawerContentStyles();
  const props = alertProps(
    t,
    classes,
    certification,
    onAction,
    canPurchaseOnline
  );

  if (props) {
    return (
      <div className={classes.alertContainer}>
        <Alert {...props} closeButtonEventHandler={() => undefined} />
      </div>
    );
  }
};

const EarnedContent = ({
  certification,
  onAction,
  canPurchaseOnline,
}: {
  certification: Exclude<Certification, ElectiveWithType> | undefined;
  onAction: CertificationActionCallback;
  canPurchaseOnline: boolean;
}) => {
  const { t } = useTranslation();

  if (!certification) return null;

  // TS-1841 Drawer expires date
  const retrieveExpiresDate = () => {
    if (
      certification.type === "CORE" &&
      (certification.goldStatus === "EARNED" ||
        certification.goldStatus === "EXPIRES_SOON")
    ) {
      return "-";
    }

    return certification.expirationDate
      ? formatDate(new Date(certification.expirationDate), "date")
      : "-";
  };

  const fields =
    certification.type === "CREDENTIAL"
      ? [
          {
            label: t("training.attributes.earned.label"),
            value: certification.earnDate
              ? formatDate(new Date(certification.earnDate), "date")
              : "-",
          },
        ]
      : [
          {
            label: t("training.attributes.earned.label"),
            value: certification.earnDate
              ? formatDate(new Date(certification.earnDate), "date")
              : "-",
          },
          {
            label: t("training.attributes.certification_method.label"),
            value: capitalize(certification.earnMethod),
          },
          {
            label: t("training.attributes.expires.label"),
            value: retrieveExpiresDate(),
          },
        ];
  return (
    <>
      <EarnedContentAlert
        certification={certification}
        onAction={onAction}
        canPurchaseOnline={canPurchaseOnline}
      />

      <InfoDrawerMeta
        heading={t("training.headings.details")}
        parentTestId="training-drawer"
        fields={fields}
      />

      <HistorySection certification={certification} onAction={onAction} />
    </>
  );
};

export default EarnedContent;
